import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import { useEffect, useState } from 'react';
import { admin, projectAssignments, type ProjectMember, type UserAdmin } from '@/api/endpoints';
import { Icon } from './Icon';
import { SearchableSelect } from '@/components/SearchableSelect';

interface Props {
  studyId: number;
  canManage: boolean;
}

const POZIOMY = [
  { value: 'VIEW', label: 'Podgląd', color: 'pill' },
  { value: 'EDIT', label: 'Edit', color: 'pill-water' },
  { value: 'SIGN', label: 'Podpis', color: 'pill-ai' },
  { value: 'MANAGE', label: 'Zarządzanie', color: 'pill-amber' },
];

export function TeamMembers({ studyId, canManage }: Props) {
  const [members, setMembers] = useState<ProjectMember[]>([]);
  const [users, setUsers] = useState<UserAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => { void load(); }, [studyId]);

  async function load() {
    setLoading(true); setErr(null);
    try {
      setMembers(await projectAssignments.list(studyId));
      if (canManage) {
        try { setUsers(await admin.users.list()); } catch { /* non-admin */ }
      }
    } catch (e: any) {
      setErr(e.message ?? 'Błąd');
    } finally { setLoading(false); }
  }

  async function removeMember(uid: number) {
    if (!confirm('Usunąć z zespołu?')) return;
    try {
      await projectAssignments.remove(studyId, uid);
      await load();
    } catch (e: any) { setErr(e.message); }
  }

  if (loading) return <div className="text-ink-400 text-[13px]">Ładowanie zespołu…</div>;

  return (
    <div className="card">
      <div className="card-head">
        <div className="card-title">Zespół projektu</div>
        {canManage && (
          <button onClick={() => setAdding(true)} className="btn btn-secondary btn-sm ml-auto">
            <Icon name="plus" className="w-3.5 h-3.5" /> Dodaj
          </button>
        )}
      </div>
      <div className="card-body !px-[18px] !py-1.5">
        {err && <div className="text-red-500 text-[12px] py-2">{err}</div>}
        {members.length === 0 && !err && (
          <div className="text-ink-400 text-[12px] py-2">Brak przypisanych użytkowników.</div>
        )}
        {members.map(m => (
          <div key={m.id} className="flex items-center gap-3 py-2 border-b border-ink-100 last:border-b-0">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#1e3a8a] to-water-500 text-white text-[11px] font-semibold grid place-items-center">
              {m.initials}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-medium">{m.fullName}</div>
              <div className="text-[11px] text-ink-400">
                <span className="font-mono">{m.login}</span>
                {m.category !== 'NONE' && <span> · {m.category}</span>}
                {m.note && <span> · {m.note}</span>}
              </div>
            </div>
            <span className={'pill text-[10px] ' + (POZIOMY.find(p => p.value === m.accessLevel)?.color ?? '')}>
              {POZIOMY.find(p => p.value === m.accessLevel)?.label ?? m.accessLevel}
            </span>
            {canManage && (
              <button onClick={() => void removeMember(m.userId)}
                      className="btn btn-ghost btn-icon text-red-500"
                      title="Usuń z zespołu">
                <Icon name="x" className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        ))}
      </div>
      {adding && canManage && (
        <AddMemberDialog
          studyId={studyId}
          allUsers={users}
          existing={members.map(m => m.userId)}
          onClose={() => { setAdding(false); void load(); }}
        />
      )}
    </div>
  );
}

function AddMemberDialog({ studyId, allUsers, existing, onClose }: {
  studyId: number;
  allUsers: UserAdmin[];
  existing: number[];
  onClose: () => void;
}) {
  const [userId, setUserId] = useState<number | ''>('');
  const [poziom, setPoziom] = useState('EDIT');
  const [note, setNote] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const dostepni = allUsers.filter(u => u.active && !existing.includes(u.id));

  async function zapisz() {
    if (!userId) { setErr('Wybierz użytkownika'); return; }
    setBusy(true); setErr(null);
    try {
      await projectAssignments.add(studyId, Number(userId), poziom, note || undefined);
      onClose();
    } catch (e: any) {
      setErr(e.message ?? 'Błąd');
    } finally { setBusy(false); }
  }

  const { requestClose, confirmation, dialogProps } = useUnsavedChanges({
    value: { userId, poziom, note }, onClose, busy,
  });

  return (
    <div className="fixed inset-0 bg-black/50 grid place-items-center z-50 p-5" {...dialogProps}>
      {confirmation}
      <div className="card max-w-[500px] w-full">
        <div className="card-head">
          <div className="card-title">Dodaj do zespołu</div>
          <button onClick={requestClose} className="ml-auto btn btn-ghost btn-icon"><Icon name="x" /></button>
        </div>
        <div className="card-body space-y-3">
          {err && <div className="p-2 bg-red-100 text-red-500 rounded text-[12px]">{err}</div>}
          <label className="field">
            <span className="field-label">Użytkownik</span>
            <SearchableSelect value={userId === '' ? '' : String(userId)}
                    onChange={v => setUserId(v === '' ? '' : Number(v))}>
              <option value="">— wybierz —</option>
              {dostepni.map(u => (
                <option key={u.id} value={u.id}>
                  {u.fullName} ({u.login}) · {(u.roles ?? []).join(',')} · {u.category}
                </option>
              ))}
            </SearchableSelect>
          </label>
          <label className="field">
            <span className="field-label">Poziom dostępu</span>
            <SearchableSelect value={poziom} onChange={setPoziom}>
              <option value="VIEW">Podgląd</option>
              <option value="EDIT">Edycja</option>
              <option value="SIGN">Podpis (wymaga odpowiedniej kategorii uprawnień)</option>
              <option value="MANAGE">Pełne zarządzanie</option>
            </SearchableSelect>
          </label>
          <label className="field">
            <span className="field-label">Note (opcjonalnie)</span>
            <input className="input" value={note} onChange={e => setNote(e.target.value)}
                   placeholder="np. Odpowiedzialny za analizy hydrogeologiczne" />
          </label>
          <div className="flex gap-2 justify-end pt-3 border-t border-ink-100">
            <button onClick={requestClose} className="btn btn-secondary btn-sm">Anuluj</button>
            <button disabled={busy || !userId} onClick={() => void zapisz()} className="btn btn-primary btn-sm">
              {busy ? 'Zapisuję…' : 'Dodaj'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
